import React, { useState, useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { Button } from '../../components/base/Button';
import { updateSEO, seoData } from '../../utils/seo';

const BuyersPage: React.FC = () => {
  useEffect(() => {
    updateSEO(seoData.buyers);
  }, []);

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    country: '',
    monthlyVolume: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d45j63vgaed3g6l5210g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          country: '',
          monthlyVolume: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(8, 145, 178, 0.8)), url('https://public.readdy.ai/ai/img_res/0a6b3e44572083fd1651172bc5ed90e0.jpg')`
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Reliable Supply for <strong>Nigerian Buyers</strong>
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Consistent supply of export-grade hides with full documentation support and logistics coordination
          </p>
        </div>
      </section>

      {/* Why Choose Henatos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Why Choose <strong>Henatos</strong> as Your Supplier?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide Nigerian and ECOWAS buyers with reliable, high-quality hides and comprehensive support
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-truck-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Consistent Supply</h3>
              <p className="text-gray-600">
                Regular shipments of 5+ containers monthly ensuring steady inventory for your operations.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Quality Assurance</h3>
              <p className="text-gray-600">
                99% quality compliance with rigorous inspection and grading processes for every shipment.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-text-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Documentation Support</h3>
              <p className="text-gray-600">
                Complete import documentation and customs clearance support at Lagos ports.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Reliable Timing</h3>
              <p className="text-gray-600">
                Predictable 18-25 day transit times from African ports to Lagos with tracking support.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-price-tag-3-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Competitive Pricing</h3>
              <p className="text-gray-600">
                Direct sourcing from African suppliers ensures competitive pricing and value.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Dedicated Support</h3>
              <p className="text-gray-600">
                Personal account management and responsive customer service throughout the process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Details */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Export-grade%20air-dried%20cow%20hides%20ready%20for%20shipment%2C%20professional%20packaging%20and%20baling%20process%2C%20quality%20control%20inspection%2C%20premium%20hides%20sorted%20by%20weight%20and%20grade%20for%20Nigerian%20buyers&width=600&height=400&seq=buyers-product&orientation=landscape"
                alt="Export-grade hides for Nigerian buyers"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Export-Grade <strong>Product Specifications</strong>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Our air-dried cow hides are specifically processed for the Nigerian market, 
                meeting all requirements for Pomo Ijebu production with consistent quality and sizing.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700"><strong>Weight Range:</strong> 6.5kg - 12kg per piece</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700"><strong>Processing:</strong> Air-dried only, no salt treatment</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700"><strong>Moisture Content:</strong> 8-12% optimal range</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700"><strong>Packaging:</strong> Professional baling for container shipping</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700"><strong>Origin:</strong> Tanzania, Burundi, Zambia, Zimbabwe</span>
                </div>
              </div>
              
              <Button href="#inquiry-form" variant="primary">
                Request Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Logistics & Ports */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Logistics & <strong>Port Operations</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Streamlined shipping and customs clearance through major Nigerian ports
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-blue-900 mb-6">Shipping Routes</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-ship-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-900 mb-2">Dar es Salaam → Lagos</h4>
                    <p className="text-gray-600">Primary route from Tanzania with 18-22 day transit time</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-ship-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-900 mb-2">Beira → Lagos</h4>
                    <p className="text-gray-600">Southern Africa route via Mozambique, 20-25 day transit</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-ship-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-900 mb-2">Durban → Lagos</h4>
                    <p className="text-gray-600">Alternative route from South Africa, 22-25 day transit</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-blue-900 mb-6">Nigerian Ports</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-900 mb-2">Apapa Port</h4>
                    <p className="text-gray-600">Primary destination with full customs and documentation support</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-900 mb-2">Tin Can Port</h4>
                    <p className="text-gray-600">Alternative clearance point with efficient processing</p>
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-blue-900 mb-2">Documentation Included</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Bill of Lading</li>
                    <li>• Certificate of Origin</li>
                    <li>• Packing List</li>
                    <li>• Quality Certificate</li>
                    <li>• Import License Support</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buyer Inquiry Form */}
      <section id="inquiry-form" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Buyer <strong>Inquiry Form</strong>
            </h2>
            <p className="text-xl text-gray-600">
              Get a customized quote for your hides and skins requirements
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <form onSubmit={handleSubmit} data-readdy-form id="buyer-inquiry">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Your company name"
                  />
                </div>
                
                <div>
                  <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Full name of contact person"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="company@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="+234 123 456 7890"
                  />
                </div>
                
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select your country</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="Ghana">Ghana</option>
                    <option value="Benin">Benin</option>
                    <option value="Togo">Togo</option>
                    <option value="Burkina Faso">Burkina Faso</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="monthlyVolume" className="block text-sm font-medium text-gray-700 mb-2">
                    Required Monthly Volume *
                  </label>
                  <input
                    type="text"
                    id="monthlyVolume"
                    name="monthlyVolume"
                    value={formData.monthlyVolume}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="e.g., 1 container, 20 tons"
                  />
                </div>
              </div>
              
              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Thank you for your inquiry! Our team will contact you within 24 hours with a detailed quote.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  There was an error submitting your inquiry. Please try again or contact us directly.
                </div>
              )}
              
              <div className="mt-8">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ready to Start <strong>Importing</strong> with Henatos?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join our network of satisfied Nigerian buyers who rely on Henatos for consistent, 
            high-quality hides and professional service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="mailto:accounts@henatosbiz.com" variant="secondary" size="lg">
              Email Us
            </Button>
            <Button href="https://wa.me/message" variant="outline" size="lg">
              WhatsApp
            </Button>
            <Button href="/contact" variant="primary" size="lg">
              Contact Page
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BuyersPage;
